/**
 * Turning a click into a walk.
 *
 * Every movement in the game goes through here: a click, a held drag, a chase, and the
 * per-frame advance from one leg of a route to the next. That is deliberate —
 * prediction and the `CMD_MOVE_TO` that goes with it must happen together and in
 * that order, and having four call sites do it by hand is how they drift.
 *
 * The routing itself is in `pathfind.ts`, which explains why it lives on the client.
 * This file is the part that has to be exactly right about ordering:
 *
 *  - **Predict, then send.** Moving on the frame of the click is the whole point of
 *    prediction; waiting for the server costs a tick no smoothing can hide.
 *  - **A chase keeps its route; every other attack abandons one.** This is the rule the
 *    click-to-chase control model reversed, and it is worth being precise about what
 *    changed. A route used to be dropped on *any* attack order, because the server
 *    stopped movement on one and a route still feeding it legs would fight it for the
 *    rest of the path. A chase's legs are flagged `chase`, which is the one move order
 *    the server does **not** let clear an attack target — so the two orders coexist,
 *    the player walks and swings, and the route is the thing carrying them into range.
 *    A shift-attack, a ground attack and the ordinary attack a chase sends *on arrival*
 *    all still drop it, and must: those are the commands that mean "stand here".
 *  - **A zone change abandons the route.** The waypoints were tiles in the zone that
 *    was just left; walking them in the new one is walking into walls.
 *  - **A dodge abandons the route.** A roll is movement the player does not steer, and
 *    the server drops the move order when it starts one; a route still feeding legs
 *    would fight the roll for its whole duration and then resume walking somewhere the
 *    player has been carried away from.
 */

import { attackTarget, dodge, moveTo, type Command, type ZonePayload } from '../net/protocol';
import type { PlayerPredictor } from '../net/predict';
import { findPath, Route, type Point } from './pathfind';
import { clearShot } from './zone/fixtures';

/**
 * How often a chase may recompute its path, in milliseconds, and how far the quarry
 * must have moved for it to bother.
 *
 * Both gates, for the reason a held drag has both: a monster in a fight jitters by
 * fractions of a unit every snapshot, and re-running A* over the grid on every frame of
 * a chase is work nobody can see. 150 ms is about five server ticks — a monster walking
 * at three units a second has moved half a unit in that time, which is far inside the
 * reach the chase is walking into.
 *
 * The distance gate is what makes a chase across an empty room cost one path and not
 * forty. The route is still advanced every frame in between; only the *search* is
 * throttled.
 */
export const CHASE_REPATH_INTERVAL_MS = 150;
export const CHASE_REPATH_DISTANCE = 1.0;

/**
 * How far inside the player's own reach a chase stops, in world units.
 *
 * Hysteresis, not a fudge. The chase stops at `attackRange - CHASE_STOP_MARGIN` and only
 * resumes past `attackRange`, so a monster shuffling on the spot at exactly the edge of
 * the reach cannot make the player start and stop walking every frame — which reads as a
 * stutter and puts a command on the socket for each flip.
 *
 * Stopping *inside* the reach rather than on it also means the server, whose copy of the
 * monster's position is a tick ahead of the client's, still finds the target in range.
 */
export const CHASE_STOP_MARGIN = 0.35;

/** What a navigator needs from the store. Narrow, so a test needs no store. */
export interface NavigatorHost {
  zone(): ZonePayload | null;
  send(command: Command): void;
  /**
   * The roll's trajectory, from WELCOME. Read through the host rather than passed in
   * once because the first roll can precede the first frame, and a hard-coded default
   * here would be a second copy of a server number.
   */
  dodgeDistance(): number;
  dodgeDuration(): number;
  /**
   * Whether a roll would actually be granted — cooldown and stamina, the same two
   * conditions the server checks. **This is what stops the roll being a rubber-band.**
   */
  dodgeState(): { ready: boolean };
  /**
   * Where a live entity is right now, or null when it has died or left the snapshot.
   *
   * Read off the store's raw map — the render loop's non-reactive surface — rather than
   * off the interpolation buffer, because the question a chase asks is "would the server
   * consider me in range", and the raw snapshot is the closest thing the client has to
   * the server's own copy. A buffer sample is deliberately 66 ms in the past.
   */
  entityAt(id: string): Point | null;
  /**
   * The zone's fallen fixtures, for the line-of-sight half of "in reach" (`clearShot`). Optional:
   * absent is everything standing, which is what a test without doors wants.
   */
  fixturesDown?(): ReadonlySet<number>;
  /**
   * How far a swing reaches, from WELCOME.
   *
   * The client routes the chase, so it is the client that has to know where to stop
   * walking — but it only ever *reads* this. Whether a swing lands is
   * `resolvePlayerAttack`'s decision on the server and nothing here changes it.
   */
  attackRange(): number;
}

/**
 * Something to do on arriving somewhere — walking to an artisan and opening their shop.
 *
 * A callback rather than anything the Navigator understands, so this file stays about
 * movement: it knows a radius and a point, and nothing about workshops.
 */
export interface ArrivalIntent {
  at: Point;
  radius: number;
  done: () => void;
}

export class Navigator {
  private readonly route = new Route();

  /** The entity being chased, or null. */
  private chaseId: string | null = null;
  /**
   * True once the chase has told the server to stand and swing.
   *
   * The chase does not end when it arrives — the quarry moves — so this is the half of
   * the state machine that says which of the two orders is currently live.
   */
  private holding = false;
  /** Where the quarry was when the path was last computed, and when that was. */
  private lastSearch: { at: Point; when: number } | null = null;

  /** What to do on arriving, or null. Dropped by any new order. */
  private arrival: ArrivalIntent | null = null;

  constructor(
    private readonly predictor: PlayerPredictor,
    private readonly host: NavigatorHost,
  ) {}

  /** Waypoints left to walk. For diagnostics and tests. */
  get remaining(): number {
    return this.route.remaining;
  }

  /** The entity being chased, or null. For diagnostics and tests. */
  get chasing(): string | null {
    return this.chaseId;
  }

  /**
   * Walks to a world point, routing round walls on the way.
   *
   * Falls back to a straight line when there is no zone yet — the very first
   * clicks after connecting can land before the ZONE frame does, and refusing to
   * move then would look like the bug this replaced.
   *
   * `arrival` is an optional thing to do on getting there, which is how clicking an
   * artisan opens their shop. It is dropped by the next order of any kind, because a
   * player who clicks elsewhere has changed their mind.
   */
  /** Where the player is on screen: the predicted position. */
  get position(): Point {
    return this.predictor.current;
  }

  goTo(x: number, z: number, arrival?: ArrivalIntent): void {
    // A new destination is a new intent, so whatever was being chased is abandoned —
    // and this is the plain `CMD_MOVE_TO` that tells the server so, since an ordinary
    // move order still clears the attack target.
    this.chaseId = null;
    this.holding = false;
    this.lastSearch = null;
    this.arrival = arrival ?? null;

    this.pathTo({ x, z }, false);
  }

  /**
   * Walks into range of a monster and attacks it, following if it moves.
   *
   * **Both orders go out, in this order, and the order matters.** `CMD_ATTACK_TARGET`
   * establishes the target and stops movement; the chase leg immediately after starts
   * the walk again and, being flagged `chase`, leaves the target alone. Commands arrive
   * in sequence on one socket, so the tick ends with the player holding both — which is
   * what lets the server swing on the tick the range is met rather than after the client
   * notices and pays a round trip to say so.
   *
   * The routing is entirely the client's, as every other walk here is. The server never
   * picks a destination; it receives straight-line legs it would have accepted anyway.
   */
  chase(id: string, at: Point, now: number): void {
    this.arrival = null;
    this.chaseId = id;
    this.holding = false;
    this.lastSearch = null;

    this.host.send(attackTarget(id));
    this.search(at, now);
  }

  /**
   * Advances the route, the chase and any arrival intent. Called once a frame.
   *
   * Reads the predicted position rather than the server's: the predictor is where
   * the player *is* on screen, and waiting for the server to confirm each corner
   * would stall for a round trip at every one of them.
   */
  tick(now: number = 0): void {
    if (this.chaseId !== null) {
      this.tickChase(this.chaseId, now);
    } else {
      const next = this.route.advance(this.predictor.current);
      if (next) this.walk(next, false);
    }

    this.tickArrival();
  }

  /**
   * Rolls toward a world point, or along the current facing when given none.
   *
   * Returns whether the roll was **predicted**, which is not the same as whether it
   * happened — the server decides that. The caller uses it to give the player immediate
   * local feedback, and it rides the command so a refusal can be recognised as a genuine
   * disagreement rather than an expected one.
   *
   * ── Predict only what the server will grant ─────────────────────────────────
   *
   * This method used to predict **unconditionally**, and that was the reported desync: a
   * roll covers 4 units, `RECONCILE_SNAP_DISTANCE` is 3.5, so every press the server
   * refused for cooldown or stamina ended in a visible snap back. The comment that
   * justified it claimed refusals were rare. They are not — the cooldown is 3 seconds and
   * a player in a fight presses `Space` far more often than that, so during a fight the
   * *refused* press is the common one.
   *
   * The fix is not to stop predicting; it is to check the same two conditions the server
   * checks before doing so. `store.dodgeState()` counts the cooldown down locally between
   * snapshots precisely so this decision is right at the boundary in both directions.
   *
   * **It still sends either way.** The server stays the authority on what happened and the
   * only thing that words a refusal — the same rule an empty action-bar slot follows.
   */
  roll(aim: Point | null, facing: number): boolean {
    const here = this.predictor.current;
    const direction = aim
      ? { x: aim.x - here.x, z: aim.z - here.z }
      : // The inverse of Go's Vec2.Angle, which is Atan2(X, Z) — so X is the sine.
        // Reversed, this rolls mirrored about the diagonal.
        { x: Math.sin(facing), z: Math.cos(facing) };

    // The route goes first, and unconditionally: the server clears the move order when it
    // grants a roll, and a route still feeding legs would fight it. A press it refuses
    // does not clear the order server-side — but abandoning a route the player has just
    // tried to interrupt is the behaviour they asked for either way.
    //
    // The chase goes with it, and the server agrees: `dodge` clears the attack target
    // too, so a chase surviving a roll would be a client walking after a target the
    // server has stopped swinging at.
    this.route.clear();
    this.dropChase();

    const predicted =
      this.host.dodgeState().ready &&
      this.predictor.dodge(direction, this.host.dodgeDistance(), this.host.dodgeDuration());

    // Aim is sent as the *point*, not the direction. The server normalises it against the
    // player's own position, which is the position it trusts.
    this.host.send(aim ? dodge({ x: aim.x, z: aim.z }, predicted) : dodge(undefined, predicted));
    return predicted;
  }

  /**
   * Abandons the route and halts the prediction. A shift-attack, or arriving.
   *
   * This is the "stand here" path, and it is still what an attack order that is *not*
   * part of a chase does. The caller sends whatever command stops the server; nothing is
   * sent from here.
   */
  stop(): void {
    this.route.clear();
    this.dropChase();
    this.predictor.stop();
  }

  /** Abandons the route without touching the prediction. A zone change. */
  clear(): void {
    this.route.clear();
    this.dropChase();
  }

  /** True while a roll is being predicted, so callers can leave it alone. */
  get rolling(): boolean {
    return this.predictor.dodging;
  }

  // ── The chase ─────────────────────────────────────────────────────────────

  /**
   * One frame of a chase.
   *
   * Three states, and the transitions between them are the whole of it: walking toward
   * the quarry, holding at it, and over.
   */
  private tickChase(id: string, now: number): void {
    const at = this.host.entityAt(id);
    if (!at) {
      // Dead, or gone from the snapshot. The server has already dropped the attack
      // order; what it may still hold is a move order toward the leg the player was
      // walking, so the halt below is what stops them wandering onto the corpse.
      this.endChase();
      return;
    }

    const reach = this.host.attackRange();
    const distance = Math.hypot(at.x - this.predictor.current.x, at.z - this.predictor.current.z);
    // **In reach means in reach and in sight** (`design/LEVEL-FIXTURES.md`): the server lands no
    // blow through a wall, an obstacle or a closed door, so a chase that stopped at mere distance
    // would stand behind a pillar swinging at nothing. It keeps walking until the shot is clear.
    const zone = this.host.zone();
    const sight = !zone || clearShot(zone, this.predictor.current, at, this.host.fixturesDown?.());

    if (this.holding) {
      // Still in reach: nothing to do. The server is swinging.
      if (distance <= reach && sight) return;
      // It walked off. Resume — a fresh path, because the last one ended here.
      this.holding = false;
      this.search(at, now);
      return;
    }

    if (distance <= reach - CHASE_STOP_MARGIN && sight) {
      // Arrived. An ordinary attack order is what stops the server, and it is the one
      // command that means "stand here and fight" — the chase does not need a second.
      this.holding = true;
      this.route.clear();
      this.predictor.stop();
      this.host.send(attackTarget(id));
      return;
    }

    // Still walking. Search again only when the throttle allows *and* something has
    // changed: the quarry has moved, or the route has run out with the player still
    // short of it.
    const due = !this.lastSearch || now - this.lastSearch.when >= CHASE_REPATH_INTERVAL_MS;
    const moved =
      !this.lastSearch ||
      Math.hypot(at.x - this.lastSearch.at.x, at.z - this.lastSearch.at.z) >= CHASE_REPATH_DISTANCE;

    if (due && (moved || !this.route.active)) {
      this.search(at, now);
      return;
    }

    const next = this.route.advance(this.predictor.current);
    if (next) this.walk(next, true);
  }

  /** Paths to the quarry and walks the first leg, recording the search. */
  private search(at: Point, now: number): void {
    this.lastSearch = { at: { x: at.x, z: at.z }, when: now };
    this.pathTo(at, true);
  }

  /**
   * Ends a chase and stops the server where the client is.
   *
   * The halt is only needed while the chase was *walking*: the server then holds a move
   * order toward a leg the player has stopped caring about, and letting it run would
   * carry them the rest of the way to a corpse — up to a whole leg of drift for the
   * predictor to undo. Once holding, the server is already standing still.
   */
  private endChase(): void {
    const wasWalking = !this.holding && this.predictor.walking;
    this.dropChase();
    this.route.clear();
    this.predictor.stop();
    if (wasWalking) {
      const here = this.predictor.current;
      this.host.send(moveTo(here.x, here.z, this.predictor.nextSeq()));
    }
  }

  /** Forgets the chase without sending anything. */
  private dropChase(): void {
    this.chaseId = null;
    this.holding = false;
    this.lastSearch = null;
  }

  // ── Arriving ──────────────────────────────────────────────────────────────

  private tickArrival(): void {
    const intent = this.arrival;
    if (!intent) return;

    const here = this.predictor.current;
    if (Math.hypot(intent.at.x - here.x, intent.at.z - here.z) <= intent.radius) {
      // Cleared *before* firing, so a callback that issues another order — which is
      // exactly what opening a shop does not do, but the next one might — cannot be
      // undone by this method finishing.
      this.arrival = null;
      intent.done();
      return;
    }

    // The walk ended short. A path into a corner, a wall the pathfinder could not get
    // round, or a click on a spot the geometry does not allow. Drop the intent rather
    // than leaving it armed: it would otherwise fire the next time the player happened
    // to wander past, long after they had forgotten asking.
    if (!this.route.active && !this.predictor.walking) this.arrival = null;
  }

  // ── Sending ───────────────────────────────────────────────────────────────

  /** Routes to a point and walks the first leg. */
  private pathTo(to: Point, chase: boolean): void {
    const zone = this.host.zone();
    if (!zone) {
      this.route.clear();
      this.walk(to, chase);
      return;
    }

    const path = findPath(zone, this.predictor.current, to);
    // No route at all: send the straight line anyway. The server will stop the
    // player at the wall, which is the old behaviour — worse than routing, but
    // better than a click that does nothing, and it keeps a hole in the
    // pathfinder from making the game unplayable.
    const first = this.route.start(path) ?? to;
    this.walk(first, chase);
  }

  private walk(to: Point, chase: boolean): void {
    this.predictor.moveTo(to);
    this.host.send(moveTo(to.x, to.z, this.predictor.nextSeq(), chase));
  }
}
